import { UGS_SHEET_MODES } from 'constants/unguided-sheet';

import { AV_EVENTS } from '@cuemath/analytics';

import cueAnalytics from './cueAnalytics';
import sentryTracker from './sentry';
import webengageTracker from './webengage';

const TRACKING_CHANNELS = {
  SENTRY: 'SENTRY',
  WEBENGAGE: 'WEBENGAGE',
  CUEANALYTICS: 'CUE_ANALYTICS',
};

const TRACKERS = {
  [TRACKING_CHANNELS.WEBENGAGE]: webengageTracker,
  [TRACKING_CHANNELS.SENTRY]: sentryTracker,
  [TRACKING_CHANNELS.CUEANALYTICS]: cueAnalytics.track,
};

export const PROJECT_TRACKING_CHANNELS = {
  DEBUG: [TRACKERS[TRACKING_CHANNELS.WEBENGAGE]],
  INFO: [TRACKERS[TRACKING_CHANNELS.WEBENGAGE]],
  WARN: [TRACKERS[TRACKING_CHANNELS.WEBENGAGE]],
  ERROR: [TRACKERS[TRACKING_CHANNELS.WEBENGAGE]],
  FATAL: [TRACKERS[TRACKING_CHANNELS.WEBENGAGE]],
  OFF: [TRACKERS[TRACKING_CHANNELS.WEBENGAGE]],
  TRACE: [TRACKERS[TRACKING_CHANNELS.WEBENGAGE]],
  DATA: [TRACKERS[TRACKING_CHANNELS.CUEANALYTICS]],
  SENTRY: [TRACKERS[TRACKING_CHANNELS.SENTRY]],
};

const AV_WEBANGAGE_EVENT_TYPE = {
  CUE_CALL: 'CUE_CALL', // inhouse av call
  AV_PLAYER: 'AV_PLAYER', // tokbox avcall
  SCREEN_GRABBER: 'SCREEN_GRABBER', // tokbox scree-grab call
  SCREEN_SHARE: 'SCREEN_SHARE', // tokbox screen-share call
  BROADCAST_PLAYER: 'BROADCAST_PLAYER', // tokbox broadcast call
  HMS_BROADCAST: 'HMS_BROADCAST', // 100ms broadcast call
  HMS_PLAYER: 'HMS_PLAYER', // 100ms av call
  HMS_SCREEN_GRABBER: 'HMS_SCREEN_GRABBER', // 100ms screen-grab call
  HMS_SCREEN_SHARE: 'HMS_SCREEN_SHARE', // 100ms screen-share call
  SET_PRIMARY_MEDIA_PROVIDER: 'SET_PRIMARY_MEDIA_PROVIDER', // Calculated final 3rd party media-provider
  SET_RM_CALL_FLAG: 'SET_RM_CALL_FLAG', // Calculated final rm-call-allowed flag
  PREVIEW_ENTER_CLASS: 'PREVIEW_ENTER_CLASS', // Enter-class preview screen event
  GROUP_CALL: 'GROUP_CALL', // Group call events and errors
};

const MASTERY_PLAN_EVENTS = {
  //* Sheet config events.
  SHEET_QUESTION_CONFIG: 'SHEET_QUESTION_CONFIG',

  //* Sheet events.
  SHEET_HINT_SEEN: 'SHEET_HINT_SEEN',
  SHEET_NAVIGATION_BACKWARD: 'SHEET_NAVIGATION_BACKWARD',
  SHEET_NAVIGATION_FORWARD: 'SHEET_NAVIGATION_FORWARD',
  SHEET_QUESTION_EDIT: 'SHEET_QUESTION_EDIT',
  SHEET_QUESTION_SAVE: 'SHEET_QUESTION_SAVE',
  SHEET_QUESTION_SAVE_AND_PROCEED: 'SHEET_QUESTION_SAVE_AND_PROCEED',
  SHEET_RANDOM_QUESTION_VISIT: 'SHEET_RANDOM_QUESTION_VISIT',
  SHEET_SKIPPED_QUESTION: 'SHEET_SKIPPED_QUESTION',
  SHEET_SUBMISSION: 'SHEET_SUBMISSION',
  SHEET_SEE_ALL_ITEMS: 'SHEET_SEE_ALL_ITEMS',
  SHEET_SUMMARY_OPENED: 'SHEET_SUMMARY_OPENED',
  SP_INCLASS_SHEET_START: 'SP_INCLASS_SHEET_START',
  SP_INLCASS_SHEET_RESUME: 'SP_INLCASS_SHEET_RESUME',
  SP_SHEET_NAME_LOG_FAIL: 'SP_SHEET_NAME_LOG_FAIL',
  SP_SHEET_START_RESUME_FAIL: 'SP_SHEET_START_RESUME_FAIL',
  AUTO_SUBMISSION_EVENT_TRIGGER: 'AUTO_SUBMISSION_EVENT_TRIGGER',

  //* Non sheet events.
  MASTERY_SHEET_START: 'MASTERY_SHEET_START',
  MASTERY_SHEET_RESUME: 'MASTERY_SHEET_RESUME',
  MASTERY_SHOW_INSTRUCTIONS: 'MASTERY_SHOW_INSTRUCTIONS',
  MASTERY_OPEN_REVIEW_SHEET: 'MASTERY_OPEN_REVIEW_SHEET',
  MASTERY_DASHBOARD_OPENED: 'MASTERY_DASHBOARD_OPENED',
  MASTERY_SHEET_AUTO_SUBMISSION_WARNING: 'MASTERY_SHEET_AUTO_SUBMISSION_WARNING',
  MASTERY_WORKSHEET_EXIT_WARNING: 'MASTERY_WORKSHEET_EXIT_WARNING',
  MASTERY_INTRO_MODAL_SHOWN: 'MASTERY_INTRO_MODAL_SHOWN',
  MASTERY_INTRO_MODAL_VISITED_CONFIRMATION: 'MASTERY_INTRO_MODAL_VISITED_CONFIRMATION',

  //* Chat
  MASTERY_REMINDER_MESSAGE_SENT: 'MASTERY_REMINDER_MESSAGE_SENT',
  MASTERY_REMINDER_MESSAGE_ACKNOWLEDGED: 'MASTERY_REMINDER_MESSAGE_ACKNOWLEDGED',

  //* InClass SP
  OPEN_IN_CLASS_SP_SUMMARY: 'OPEN_IN_CLASS_SP_SUMMARY',
  SHOW_SP_INSTRUCTIONS_ANIME: 'SHOW_SP_INSTRUCTIONS_ANIME',
  SP_END_REVIEW_CELEBRATION: 'SP_END_REVIEW_CELEBRATION',
};

export const ANALYTICS_EVENT = {
  LEARNOSITY_SCRIPT_LOADING: 'LEARNOSITY_SCRIPT_LOADING',
  LEARNOSITY_SCRIPT_LOADING_FAILED: 'LEARNOSITY_SCRIPT_LOADING_FAILED',
  LEARNOSITY_SCRIPT_LOADING_SUCCESS: 'LEARNOSITY_SCRIPT_LOADING_SUCCESS',
  LEARNOSITY_BACKUP_SCRIPT_LOADING_SUCCESS: 'LEARNOSITY_BACKUP_SCRIPT_LOADING_SUCCESS',
  LEARNOSITY_BACKUP_SCRIPT_LOADING_FAILED: 'LEARNOSITY_BACKUP_SCRIPT_LOADING_FAILED',

  LEARNOSITY_INITIALISING: 'LEARNOSITY_INITIALISING',
  LEARNOSITY_INIT_FAILED: 'LEARNOSITY_INIT_FAILED',
  LEARNOSITY_ERROR_MODAL_LOADED: 'LEARNOSITY_ERROR_MODAL_LOADED',
  LEARNOSITY_INIT_SUCCESS: 'LEARNOSITY_INIT_SUCCESS',
  LEARNOSITY_UNMOUNTED: 'LEARNOSITY_UNMOUNTED',

  LEARNOSITY_INIT_SAVE_FAILED: 'LEARNOSITY_INIT_SAVE_FAILED',
  LEARNOSITY_INIT_SAVE_SUCCESS: 'LEARNOSITY_INIT_SAVE_SUCCESS',

  LEARNOSITY_ITEMS_RENDERD: 'LEARNOSITY_ITEMS_RENDERD',

  LEARNOSITY_RENDER_MATH_JAX_STARTED: 'LEARNOSITY_RENDER_MATH_JAX_STARTED',
  LEARNOSITY_RENDER_MATH_JAX_SUCCESS: 'LEARNOSITY_RENDER_MATH_JAX_SUCCESS',
  LEARNOSITY_RENDER_MATH_JAX_FAILED: 'LEARNOSITY_RENDER_MATH_JAX_FAILED',

  SHEET_OPEN_API_STARTED: 'SHEET_OPEN_API_STARTED',
  SHEET_OPEN_API_SUCCESS: 'SHEET_OPEN_API_SUCCESS',
  SHEET_OPEN_API_FAILED: 'SHEET_OPEN_API_FAILED',

  SHEET_LOADING_END: 'SHEET_LOADING_END',
  SHEET_LOADING_START: 'SHEET_LOADING_START',
};

const MATH_CANVAS_ONBOARDING_EVENTS = {
  MATH_CANVAS_INTRO_MODAL_OPENED: 'MATH_CANVAS_INTRO_MODAL_OPENED',
  MATH_CANVAS_INTRO_MODAL_CLOSED: 'MATH_CANVAS_INTRO_MODAL_CLOSED',
  SCRIBBLE_COACH_MARKS_STARTED: 'SCRIBBLE_COACH_MARKS_STARTED',
  WORKSHEET_COACH_MARKS_STARTED: 'WORKSHEET_COACH_MARKS_STARTED',
  QUESTION_ACTION_BAR_JOURNEY_STARTED: 'QUESTION_ACTION_BAR_JOURNEY_STARTED',
};

const MATH_CANVAS_EVENT_TYPES = {
  BOTTOM_FAB_CLICK: 'BOTTOM_FAB_CLICK',

  WIDGET_ELEMENTS_INIT_HEIGHT_DONE: 'WIDGET_ELEMENTS_INIT_HEIGHT_DONE', // based on question content
  WIDGET_HEADER_RENDERED: 'WIDGET_HEADER_RENDERED',

  CREATE_POLYPAD_STARTED: 'CREATE_POLYPAD_STARTED_V3',
  CREATE_POLYPAD_FAILED: 'CREATE_POLYPAD_FAILED_V3',
  CREATE_POLYPAD_RENDERED: 'CREATE_POLYPAD_RENDERED_V3',

  WIDGET_RENDER_SUCCESS: 'WIDGET_RENDER_SUCCESS_V3',

  UPLOAD_S3_CANVAS_DATA: 'UPLOAD_S3_CANVAS_DATA',
  DOWNLOAD_S3_CANVAS_DATA: 'DOWNLOAD_S3_CANVAS_DATA',

  IMPORT_POLYPAD_STARTED: 'IMPORT_POLYPAD_STARTED',
  IMPORT_POLYPAD_SUCCESS: 'IMPORT_POLYPAD_SUCCESS',
  IMPORT_POLYPAD_FAILURE: 'IMPORT_POLYPAD_FAILURE',

  INDEX_DB_CREATE_STARTED: 'INDEX_DB_CREATE_STARTED',
  INDEX_DB_CREATE_SUCCESS: 'INDEX_DB_CREATE_SUCCESS',
  INDEX_DB_CREATE_FAILED: 'INDEX_DB_CREATE_FAILED',

  INDEX_DB_FETCH_STARTED: 'INDEX_DB_FETCH_STARTED',
  INDEX_DB_FETCH_SUCCESS: 'INDEX_DB_FETCH_SUCCESS',
  INDEX_DB_FETCH_FAILED: 'INDEX_DB_FETCH_FAILED',

  REDO_CLICK: 'REDO_CLICK',
  STROKE_ADDED: 'STROKE_ADDED',
  STROKE_DELETED: 'STROKE_DELETED',
  STROKE_ANALYTICS_FAILED: 'STROKE_ANALYTICS_FAILED',
  TOGGLE_SCRIBBLING: 'TOGGLE_SCRIBBLING',
  TOOL_CLICK: 'TOOL_CLICK',
  TOOL_COLOR_CLICK: 'TOOL_COLOR_CLICK',
  TOP_FAB_CLICK: 'TOP_FAB_CLICK',
  UNDO_CLICK: 'UNDO_CLICK',
  UPDATE_QUESTION_HEIGHT: 'UPDATE_QUESTION_HEIGHT',
  GREATER_WIDTH_ELEMENT: 'GREATER_WIDTH_ELEMENT',
  IS_STUDENT_POINTER_TYPE_PEN: 'IS_STUDENT_POINTER_TYPE_PEN',
  INDEX_DB_CLEANUP_SUCCESS: 'INDEX_DB_CLEANUP_SUCCESS',
  INDEX_DB_CLEANUP_FAILURE: 'INDEX_DB_CLEANUP_FAILURE',
  CLEAN_UP_INDEXED_DB_CALLING_SUCCESS: 'CLEAN_UP_INDEXED_DB_CALLING_SUCCESS',
  CLEAN_UP_INDEXED_DB_CALLING_FAILURE: 'CLEAN_UP_INDEXED_DB_CALLING_FAILURE',

  QUESTION_SUBMITTED: 'QUESTION_SUBMITTED',
  HINT_SUB_TAB_CLICK: 'HINT_SUB_TAB_CLICK',
  ...MATH_CANVAS_ONBOARDING_EVENTS,
  ...ANALYTICS_EVENT,
};

const UGS_EVENT_TYPE = {
  ...UGS_SHEET_MODES,

  UG_SHEET_AUTO_SUBMIT_DATA_SET: 'UG_SHEET_AUTO_SUBMIT_DATA_SET',
  FIVE_MIN_SHEET_END_WARNING: 'FIVE_MIN_SHEET_END_WARNING',
  UG_SHEET_AUTO_SUBMITTED: 'UG_SHEET_AUTO_SUBMITTED',
  UG_SHEET_EXPERIENCE_CONFIG: 'UG_SHEET_EXPERIENCE_CONFIG',
  UG_TEACHER_PUGNUB_EVENT: 'UG_TEACHER_PUGNUB_EVENT',
};

const TALK_METER_EVENT_TYPES = {
  TALK_CALCULATOR_STARTED: 'TALK_CALCULATOR_STARTED',
  TALK_CALCULATOR_STOPPED: 'TALK_CALCULATOR_STOPPED',
  TALK_METER_BATCH_SENT: 'TALK_METER_BATCH_SENT',
  TALK_METER_BATCH_RECEIVED: 'TALK_METER_BATCH_RECEIVED',
  TALK_METER_BATCH_DISPLAYED: 'TALK_METER_BATCH_DISPLAYED',

  TALK_METER_INTRO_MODAL_OPENED: 'TALK_METER_INTRO_MODAL_OPENED',
  TALK_METER_INTRO_MODAL_CLOSED: 'TALK_METER_INTRO_MODAL_CLOSED',
  TALK_METER_SHEET_OPENED: 'TALK_METER_SHEET_OPENED',
  TALK_METER_SHEET_COMPLETED: 'TALK_METER_SHEET_COMPLETED',
  TALK_METER_SHEET_COACH_MARK_UPDATING: 'TALK_METER_SHEET_COACH_MARK_UPDATING',
};

export const EVENT_TYPE = {
  PAGE_LOADED: 'page_loaded',
  COMPONENT_MOUNTED: 'component_mounted',
  MULTIPLE_TAB_BLOCKED: 'multiple_tab_blocked',
  MULTIPLE_TAB_BLOCKING_FAILED: 'multiple_tab_blocking_failed',
  MULTIPLE_TAB_BLOCK_POPUP_CLOSE_CLICKED: 'multiple_tab_block_popup_close_clicked',
  MULTIPLE_TAB_BLOCK_POPUP_USEHERE_CLICKED: 'multiple_tab_block_popup_usehere_clicked',

  STUDENT_LOGGED_IN: 'STUDENT_LOGGED_IN',
  STUDENT_LOGGED_OUT: 'STUDENT_LOGGED_OUT',
  APP_LISTING_CLICKED: 'APP_LISTING_CLICKED',
  AV_CALL_WINDOW: 'AV_CALL_WINDOW',
  AUTO_TRIGGERED_SHC_FOR_DAY: 'AUTO_TRIGGERED_SHC_FOR_DAY',
  ASK_TEACHER_CLICKED: 'ASK_TEACHER_CLICKED',
  BACKGROUND_INTERNET_SPEED_TEST_TIMEOUT: 'BACKGROUND_INTERNET_SPEED_TEST_TIMEOUT',
  BACKGROUND_INTERNET_SPEED_TEST: 'BACKGROUND_INTERNET_SPEED_TEST',
  BUTTON_CLICKED: 'ButtonClicked',
  CLASS_FEEDBACK_SUBMITTED: 'CLASS_FEEDBACK_SUBMITTED',
  CUE_CALL_WINDOW_LOADED: 'CUE_CALL_WINDOW_LOADED',
  CUE_CALL_CONNECTED: 'CUE_CALL_CONNETCED',
  CUE_CALL_ENDED: 'CUE_CALL_ENDED',
  DEMO_REFRESH_DONE: 'DEMO_REFRESH_DONE',
  DEMO_SUBSTEP_DONE: 'DEMO_SUBSTEP_DONE',
  DEMO_SUBSTEP_STARTED: 'DEMO_SUBSTEP_STARTED',
  DEMO_STEP_DONE: 'DEMO_STEP_DONE',
  DEMO_STEP_STARTED: 'DEMO_STEP_STARTED',
  DEBUG_LOG: 'DEBUG_LOG',
  FETCH_CLASS_HISTORY_MESSAGE_PROMISE_CATCH: 'FETCH_CLASS_HISTORY_MESSAGE_PROMISE_CATCH',
  FETCH_HISTORY_MESSAGE_CATCH: 'FETCH_HISTORY_MESSAGE_CATCH',
  FETCH_HISTORY_MESSAGE_PROMISE_CATCH: 'FETCH_HISTORY_MESSAGE_PROMISE_CATCH',
  FETCH_READ_HISTORY_MESSAGE_CATCH: 'FETCH_READ_HISTORY_MESSAGE_CATCH',
  FETCH_READ_HISTORY_MESSAGE_PROMISE_CATCH: 'FETCH_READ_HISTORY_MESSAGE_PROMISE_CATCH',
  GET_HMS_GATEKEEPER: 'GET_HMS_GATEKEEPER',
  HEALTH_CHECK_V2: 'HEALTH_CHECK_V2',
  HEALTH_CHECK: 'HEALTH_CHECK',
  HMS_TOKEN: 'HMS_TOKEN',
  CHAPTER_COMPLETE_IMAGE_FAILED: 'CHAPTER_COMPLETE_IMAGE_FAILED',
  INTERSECTION_OBSERVER_NOT_SUPPORTED: 'INTERSECTION_OBSERVER_NOT_SUPPORTED',
  LEARNOSITY_ANIMATION: 'LEARNOSITY_ANIMATION',
  LISTEN_TEACHER_TRIGGER_HEALTH_CHECK: 'LISTEN_TEACHER_TRIGGER_HEALTH_CHECK',

  MPR_VIEWED: 'MPR_VIEWED',
  MPR_NEXT_PUZZLE_VIEWED: 'MPR_NEXT_PUZZLE_VIEWED',
  MPR_PREVIOUS_PUZZLE_VIEWED: 'MPR_PREVIOUS_PUZZLE_VIEWED',
  MPR_SECTION_EXPANDED: 'MPR_SECTION_EXPANDED',
  MPR_SECTION_COLLAPSED: 'MPR_SECTION_COLLAPSED',
  MPR_SUBSECTION_VIEWED: 'MPR_SUBSECTION_VIEWED',
  PAGE_VIEWED: 'page_viewed',
  PARENT_IS_WAITING_NOTIFICATION: 'PARENT_IS_WAITING_NOTIFICATION',
  PARENT_OPENED_EXAM_PERFORMANCE_FORM: 'PARENT_OPENED_EXAM_PERFORMANCE_FORM',
  PARENT_SUBMITTED_EXAM_PERFORMANCE_FORM: 'PARENT_SUBMITTED_EXAM_PERFORMANCE_FORM',
  PREPORT_ACKNOWLEDGED: 'PREPORT_ACKNOWLEDGED',
  PREPORT_PAGE_VIEWED: 'PREPORT_PAGE_VIEWED',
  PREPORT_SCREEN_VIEWED: 'PREPORT_SCREEN_VIEWED',
  PREPORT_TAB_VIEWED: 'PREPORT_TAB_VIEWED',
  PROGRAM_SWITCHED: 'PROGRAM_SWITCHED',
  PTM: 'PTM',
  PTM_FEEDBACK_SIDE_MODAL_OPENED: 'PTM_FEEDBACK_SIDE_MODAL_OPENED',
  PTM_FEEDBACK_MODAL_OPENED: 'PTM_FEEDBACK_MODAL_OPENED',
  PTM_RESCHEDULE_CONFIRMATION_MODAL_OPENED: 'PTM_RESCHEDULE_CONFIRMATION_MODAL_OPENED',
  RESCHEDULE_PTM_MODAL_OPENED: 'RESCHEDULE_PTM_MODAL_OPENED',
  REFERRAL_MODAL_OPENED: 'REFERRAL_MODAL_OPENED',
  REFERRAL_REMIND_ME_LATER_MODAL_OPENED: 'REFERRAL_REMIND_ME_LATER_MODAL_OPENED',
  REFERRAL_REFER_NOW_BUTTON_CLICKED: 'REFERRAL_REFER_NOW_BUTTON_CLICKED',
  REFERRAL_REMINDER_ALERT: 'Referral Reminder Alert',
  SCREEN_BLOCKED: 'SCREEN_BLOCKED',
  SCREEN_DIMENSIONS: 'SCREEN_DIMENSIONS',
  SEND_REFRESH_SHEET_EVENT: 'SEND_REFRESH_SHEET_EVENT',
  SET_EVENT_DATA: 'SET_EVENT_DATA',
  STUDENT_ACTIVE: 'STUDENT_ACTIVE',
  STUDENT_ATTENDANCE_CONFIRMED: 'STUDENT_ATTENDANCE_CONFIRMED',
  STUDENT_FEEDBACK: 'STUDENT_FEEDBACK',
  STUDENT_INACTIVE: 'STUDENT_INACTIVE',
  STUDENT_LEAP_JOINED_FOR_CLASS: 'STUDENT_LEAP_JOINED_FOR_CLASS',
  STUDENT_NETWORK_ONLINE: 'STUDENT_NETWORK_ONLINE',
  STUDENT_PUBNUB_PUBLISH: 'STUDENT_PUBNUB_PUBLISH',
  STUDENT_PUBNUB_PUBLISH_SUCCESS: 'STUDENT_PUBNUB_PUBLISH_SUCCESS',
  STUDENT_PUBNUB_RECEIVE_SUCCESS: 'STUDENT_PUBNUB_RECEIVE_SUCCESS',
  STUDENT_PUBNUB_SUBSCRIBE: 'STUDENT_PUBNUB_SUBSCRIBE',
  STUDENT_PUBNUB_TIMETOKEN: 'STUDENT_PUBNUB_TIMETOKEN',
  STUDENT_SPEAKING_TIME: 'STUDENT_SPEAKING_TIME',
  TAB_WINDOW_CLOSED: 'TAB_WINDOW_CLOSED',
  TEACHER_ABSENT_PTM_ENDED_MODAL_OPENED: 'TEACHER_ABSENT_PTM_ENDED_MODAL_OPENED',
  TUCO_REPORT_VIEWED: 'TUCO_REPORT_VIEWED',
  TUCO_SLOT_BOOKING_BUTTON_CLICKED: 'TUCO_SLOT_BOOKING_BUTTON_CLICKED',
  UPDATE_LEADERBOARD: 'UPDATE_LEADERBOARD',
  UPDATE_ACTIVE_STUDENT: 'UPDATE_ACTIVE_STUDENT',
  URL_VISITED: 'URL_VISITED',
  USER_MEDIA_DIMENSION: 'USER_MEDIA_DIMENSION',
  VIBE_SHOWN: 'VIBE_SHOWN',
  VIBE_CLOSED: 'VIBE_CLOSED',
  WHITEBOARD_LOADED: 'WHITEBOARD_LOADED',
  WHITEBOARD: 'WHITEBOARD',
  WILDFIRE_CLICKED_EXPLORE_LIVE_CLASSES: 'WF Clicked Explore LIVE Classes',
  WILDFIRE_EXPLORE_LIVE_CLASSES_MODAL_CLOSE: 'WF Explore LIVE Classes Modal Close',
  WILDFIRE_REFERRAL_PAGE_OPEN: 'WF Referral Page Open',
  WORKSHEET_ERROR: 'WORKSHEET_ERROR',
  WORKSHEET_JOURNEY: 'WORKSHEET_JOURNEY',
  WORKSHEET_RELOAD_SUCCESS: 'WORKSHEET_RELOAD_SUCCESS',
  PROVIDER_SELECTED_MEDIA_DEVICES: 'PROVIDER_SELECTED_MEDIA_DEVICES',
  STUDENT_FEEDBACK_RA_MODAL_OPENED: 'STUDENT_FEEDBACK_RA_MODAL_OPENED',
  SYSTEM_HEALTH_CHECK_STARTED: 'SYSTEM_HEALTH_CHECK_STARTED',
  SHEET_SUBMIT_ERROR: 'SHEET_SUBMIT_ERROR',
  SHEET_AUTO_SUBMISSION_TRIGGER: 'SHEET_AUTO_SUBMISSION_TRIGGER',
  RESPONSE_RECOREDED: 'response_recorded',
  BUTTON_CLICKED_EVENT: 'button_clicked',
  SCREEN_VIEWED: 'screen_viewed',
  PARENT_FEEDBACK_FORM_VIEWED: 'parent_feedback_form_viewed',
  PARENT_FEEDBACK_FORM_CLICKED: 'parent_feedback_form_clicked',
  PARENT_FEEDBACK_FORM_SUBMIT: 'parent_feedback_form_submit',
  PARENT_ACKNOWLEDGEMENT_ONE_BY_FOUR: 'parent_acknowledge_one_by_four',
  GRADE_CONFIRMATION_MODAL_VIEWED: 'grade_confirmation_modal_viewed',
  GRADE_CONFIRMATION_SUBMIT_FAILED: 'grade_confirmation_submit_failed',
  STUDENT_SELECTOR_SUBMIT: 'student_selector_submit',
  ...AV_WEBANGAGE_EVENT_TYPE,
  ...MASTERY_PLAN_EVENTS,
  ...MATH_CANVAS_EVENT_TYPES,
  ...UGS_EVENT_TYPE,
  ...TALK_METER_EVENT_TYPES,
};

export const DATA_EVENT_TYPE = {
  AV_METRIC: AV_EVENTS.AV_METRIC,
  CHAPTER_OPENED: 'CHAPTER_OPENED',
  CLASSROOM_ENDED: 'CLASSROOM_ENDED',
  CLASSROOM_STARTED: 'CLASSROOM_STARTED',
  EXIT_INTENT: 'EXIT_INTENT',
  FEEDBACK_MODAL_SEEN: 'FEEDBACK_MODAL_SEEN',
  GRAB_GOODIES_CLICKED: 'GRAB_GOODIES_CLICKED',
  ITEM_OPENED: 'ITEM_OPENED',
  ITEM_SKIPPED: 'ITEM_SKIPPED',
  ITEM_SUBMITTED: 'ITEM_SUBMITTED',
  NODE_OPENED: 'NODE_OPENED',
  NODE_REVIEW: 'NODE_REVIEW',
  NODE_SUBMITTED: 'NODE_SUBMITTED',
  PROGRAM_SELECTED: 'PROGRAM_SELECTED',
  SECTION_OPENED: 'SECTION_OPENED',
  STUDENT_ACTIVE: 'STUDENT_ACTIVE',
  STUDENT_IDLE: 'STUDENT_IDLE',
  STUDENT_INACTIVE: 'STUDENT_INACTIVE',
  TAB_OPENED: 'TAB_OPENED',
  WHITEBOARD_CLOSED: 'WHITEBOARD_CLOSED',
  WHITEBOARD_OPENED: 'WHITEBOARD_OPENED',
  WIDGET_HINT_VIEWED: 'WIDGET_HINT_VIEWED',
  WIDGET_OPENED: 'WIDGET_OPENED',
  WIDGET_SUBMITTED: 'WIDGET_SUBMITTED',
  WORKSHEET_OPENED: 'WORKSHEET_OPENED',
  WORKSHEET_SUBMITTED: 'WORKSHEET_SUBMITTED',
  WORKSHEET_REVIEW: 'WORKSHEET_REVIEW',
  WHITEBOARD_TOGGLE: 'WHITEBOARD_TOGGLE',
};

export const MEDIA_PERMISSION_EVENTS = {
  AV_CAMERA_PERMISSIONS_CHANGED: 'av_camera_permissions_changed',
  AV_MICROPHONE_PERMISSIONS_CHANGED: 'av_microphone_permissions_changed',
  AV_SOUND_PERMISSIONS_CHANGED: 'av_sound_permissions_changed',
  AV_ERROR_READING_NAVIGATION_STATE: 'av_error_reading_navigation_state',
  AV_PERMISSION_BLOCKED_MODAL_APPEARED: 'av_permission_blocked_modal_appeared',
};

export const PREVIEW_MODAL_EVENTS = {
  AV_PREVIEW_MODAL_CHAT_OPENED: 'av_preview_modal_chat_opened',
  ENTER_CLASS: 'enter_class',
  NO_SHOW_MODAL_TRIGGERED: 'no_show_modal_triggered',
  NO_SHOW_MODAL_APPEARED: 'no_show_modal_appeared',
  NO_SHOW_MODAL_ACKNOWLEDGED: 'no_show_modal_acknowledged',
  ASK_TO_JOIN_WITHOUT_CAMERA: 'ask_to_join_without_camera',
  APPROVED_TO_JOIN_WITHOUT_CAMERA_AKG: 'approved_to_join_without_camera_akg',
};
